import { useMemo, useState, type ReactNode } from 'react';
import { color, font } from '../styles/theme';
import { dateTime, fullDate, plainDate, shortDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { useMeta, usePeople } from '../api/queries';
import { makeLookup, type Lookup } from '../lib/presenters';
import { statusLabelOf } from '../lib/pipeline';
import { messageOne } from '../lib/messageTypes';
import type { MetaResponse } from '../api/client';
import {
  StagePill,
  StatusPill,
  StaffTag,
  ContactFlags,
  PeopleOptions,
  DUPLICATE_TAG,
  groupRecords,
} from '../components/ContactBits';
import {
  useContact,
  useContactObjects,
  useTags,
  useSetContactTags,
  useSetContactOwner,
  useSetContactSuppressed,
  useNotDuplicate,
  isReasonRequired,
  useRecordConsent,
  useRevokeConsent,
  STOP_TEXT_MAX,
  contactTypeLabel,
  contactStageLabel,
  interestListLabel,
  type Consent,
  type ContactObjects,
  type ContactRecord,
  type TimelineEvent,
} from '../api/contacts';
import {
  Modal,
  Eyebrow,
  Field,
  Button,
  Confirm,
  Loading,
  ErrorNote,
  RedactedBadge,
  selectStyle,
} from '../components/ui';

/**
 * One person, laid out like a HubSpot record.
 *
 * The header says who they are, where they are and what must not be missed.
 * Below it, three columns: what we know about them and what staff can change
 * (owner, tags, consents, do-not-contact); their history, newest first; and
 * what is in progress — each study, referral and message with its live
 * status, a click from the record itself. Everything is within the caller's
 * studies, as the API scopes it. Opening this is an audited PHI access when
 * anything linked is PHI, same as a record.
 *
 * A stop says whose it is. One the person asked for themselves is lifted only
 * with a reason, kept on the contact with who lifted it; the API refuses the
 * lift without one (400 `details.reasonRequired`), so the form asks first. A
 * Possible duplicate flag is answered from the header with "Not a duplicate",
 * which clears it here and on the record it was paired with.
 */

const TYPE_TONE: Record<TimelineEvent['type'], { fg: string; bg: string; label: string }> = {
  submission: { fg: '#2a547e', bg: '#eef4fa', label: 'Form' },
  email: { fg: '#5b3f7a', bg: '#f1ecf6', label: 'Email' },
  ticket: { fg: '#8a4f16', bg: '#f6ddc2', label: 'Clock' },
  consent: { fg: '#14684c', bg: '#cfe9d8', label: 'Consent' },
  newsletter: { fg: '#2f6363', bg: '#eaf1f1', label: 'Newsletter' },
  suppressed: { fg: '#a4432f', bg: '#f8e3dd', label: 'Stop' },
};

const PURPOSE_LABEL: Record<Consent['purpose'], string> = {
  newsletter: 'Newsletter',
  'study-alerts': 'Study alerts',
  recontact: 'Recontact about studies',
};

export function ContactDetail({
  id,
  onClose,
  onOpenSubmission,
}: {
  id: string;
  onClose: () => void;
  onOpenSubmission?: (submissionId: string, form: string) => void;
}) {
  const detail = useContact(id);
  const objects = useContactObjects(id);
  const meta = useMeta();
  const L = useMemo(() => (meta.data ? makeLookup(meta.data) : null), [meta.data]);
  const tags = useTags();
  const { can } = useAuth();
  const canUpdate = can('submissions:update');
  const people = usePeople(canUpdate);

  const setTags = useSetContactTags();
  const setOwner = useSetContactOwner();
  const setSuppressed = useSetContactSuppressed();
  const notDuplicate = useNotDuplicate();
  const recordConsent = useRecordConsent();
  const revokeConsent = useRevokeConsent();

  const [consentPurpose, setConsentPurpose] = useState<Consent['purpose']>('newsletter');
  const [consentNote, setConsentNote] = useState('');
  const [suppressNote, setSuppressNote] = useState('');
  /** The lift form is open: a reason is asked for before the stop goes. */
  const [lifting, setLifting] = useState(false);
  const [liftReason, setLiftReason] = useState('');
  const [askDuplicate, setAskDuplicate] = useState(false);

  const c = detail.data?.contact;
  const busy =
    setTags.isPending || setOwner.isPending || setSuppressed.isPending ||
    notDuplicate.isPending || recordConsent.isPending || revokeConsent.isPending;
  const duplicate = !!c && c.tags.includes(DUPLICATE_TAG);

  // Who they are, where they are, and what must not be missed — the line a
  // coordinator reads before anything else, kept in the sticky header.
  const header = c ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingRight: 8 }}>
      <h2 style={{ fontFamily: font.display, fontWeight: 600, fontSize: 22, color: color.navy, margin: 0 }}>
        {c.name || 'No name given'}
      </h2>
      <span
        style={{
          fontSize: 11.5, fontWeight: 800, padding: '4px 11px', borderRadius: 999,
          background: color.tint, color: color.blueDeep,
        }}
      >
        {contactTypeLabel[c.type]}
      </span>
      <StagePill stage={c.stage} />
      <ContactFlags contact={c} />
      {duplicate && canUpdate && (
        <Confirm
          open={askDuplicate}
          title="Not the same person?"
          detail="The Possible duplicate flag comes off this contact and the one it was paired with, and that pair is not flagged again."
          confirmLabel="Not a duplicate"
          onConfirm={() => {
            setAskDuplicate(false);
            notDuplicate.mutate({ id: c.id });
          }}
          onCancel={() => setAskDuplicate(false)}
        >
          <button
            type="button"
            onClick={() => setAskDuplicate((v) => !v)}
            disabled={busy}
            title="They are different people: clear the flag"
            className="hov-tint"
            style={{
              background: 'transparent',
              border: `1px solid ${color.line}`,
              borderRadius: 999,
              padding: '2px 9px',
              fontSize: 11,
              fontWeight: 700,
              color: color.blueDeep,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Not a duplicate
          </button>
        </Confirm>
      )}
      {c.redacted && <RedactedBadge />}
    </div>
  ) : (
    <span />
  );

  function body() {
    if (detail.isLoading || meta.isLoading) return <Loading />;
    if (detail.error) return <ErrorNote message={(detail.error as Error).message} />;
    if (!detail.data || !c || !meta.data) return null;
    const m = meta.data;
    const { timeline, consents } = detail.data;
    const liveConsents = consents.filter((k) => !k.revokedAt);
    const staff = c.tags.filter((t) => t !== DUPLICATE_TAG);
    const availableTags = (tags.data?.tags ?? []).filter((t) => !c.tags.includes(t.slug) && t.slug !== DUPLICATE_TAG);
    const tagLabel = (slug: string) => tags.data?.tags.find((t) => t.slug === slug)?.label ?? slug;
    const open = (submissionId: string, form: string) => onOpenSubmission?.(submissionId, form);

    const boards = c.records.filter((r) => r.area === 'inbox' || r.area === 'study');
    const referrals = c.records.filter((r) => r.area === 'referral');
    const messages = c.records.filter((r) => r.area === 'message');

    return (
      <div className="contact-cols">
        {/* ── About ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          <section>
            <Eyebrow tone="blue">About</Eyebrow>
            <p style={{ fontSize: 13.5, margin: '0 0 10px', color: color.ink, overflowWrap: 'anywhere' }}>
              {c.redacted ? (
                <span style={{ color: color.muted }}>Contact details hidden for your role</span>
              ) : (
                <>
                  {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
                  {c.email && c.phone && <br />}
                  {c.phone}
                  {!c.email && !c.phone && <span style={{ color: color.muted }}>No email or phone given</span>}
                </>
              )}
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', margin: 0 }}>
              <Fact label="Who they are">{contactTypeLabel[c.type]}</Fact>
              <Fact label="Where they are">{contactStageLabel[c.stage]}</Fact>
              <Fact label="Forms sent">{String(c.submissionCount)}</Fact>
              <Fact label="Newsletter">
                {c.newsletter === 'active' ? 'Subscribed' : c.newsletter === 'unsubscribed' ? 'Unsubscribed' : 'Not on the list'}
              </Fact>
              <Fact label="First seen">{fullDate(c.firstSeenAt)}</Fact>
              <Fact label="Last seen">{fullDate(c.lastSeenAt)}</Fact>
            </dl>
          </section>

          {c.suppressedAt && (
            <p style={{ background: '#f8e3dd', color: color.red, borderRadius: 12, padding: '10px 14px', fontSize: 12.5, margin: 0 }}>
              {c.suppressionSource === 'person'
                ? `They asked us themselves to stop contacting them, on ${fullDate(c.suppressedAt)}.`
                : c.suppressionSource === 'staff'
                  ? `Staff marked them do-not-contact on ${fullDate(c.suppressedAt)}.`
                  : `Do not contact since ${fullDate(c.suppressedAt)}.`}
              {c.suppressionNote ? ` ${c.suppressionNote.replace(/\.$/, '')}.` : ''} Newsletter sends skip this person.
            </p>
          )}
          {!c.suppressedAt && c.stopLiftedAt && (
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }} title={`Lifted ${dateTime(c.stopLiftedAt)}`}>
              Stop lifted
              {c.stopLiftReason ? ` · ${c.stopLiftReason}` : ''}
              {c.stopLiftedByName ? ` · by ${c.stopLiftedByName}` : ''} · {shortDate(c.stopLiftedAt)}
            </p>
          )}

          {canUpdate ? (
            <Field label="Owner">
              <select
                value={c.ownerId ?? ''}
                disabled={busy || people.isLoading}
                onChange={(e) => setOwner.mutate({ id: c.id, ownerId: e.target.value || null })}
                style={{ ...selectStyle, fontSize: 13.5 }}
              >
                <option value="">Unassigned</option>
                <PeopleOptions people={people.data?.people} current={{ id: c.ownerId, name: c.ownerName }} />
              </select>
            </Field>
          ) : (
            <p style={{ fontSize: 13, margin: 0, color: color.ink }}>
              <span style={{ color: color.muted }}>Owner: </span>{c.ownerName ?? 'Unassigned'}
            </p>
          )}

          <section>
            <Eyebrow tone="blue">Your tags</Eyebrow>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {staff.length === 0 && <span style={{ fontSize: 12.5, color: color.muted }}>None yet.</span>}
              {staff.map((slug) => (
                <StaffTag
                  key={slug}
                  onRemove={canUpdate ? () => setTags.mutate({ id: c.id, remove: [slug] }) : undefined}
                >
                  {tagLabel(slug)}
                </StaffTag>
              ))}
            </div>
            {canUpdate && availableTags.length > 0 && (
              <select
                value=""
                disabled={busy}
                aria-label="Add a tag"
                onChange={(e) => e.target.value && setTags.mutate({ id: c.id, add: [e.target.value] })}
                style={{ ...selectStyle, fontSize: 13, marginTop: 8, width: 'auto' }}
              >
                <option value="">Add a tag…</option>
                {availableTags.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.label}</option>
                ))}
              </select>
            )}
          </section>

          <section>
            <Eyebrow tone="blue">Consents on file</Eyebrow>
            <div style={{ display: 'grid', gap: 6 }}>
              {liveConsents.length === 0 && <span style={{ fontSize: 12.5, color: color.muted }}>None recorded.</span>}
              {liveConsents.map((k) => (
                <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${color.line}` }}>
                  <span>
                    <b style={{ color: color.ink }}>{PURPOSE_LABEL[k.purpose as Consent['purpose']] ?? k.purpose}</b>
                    <span style={{ color: color.muted }}>
                      {' '}· {k.basis === 'form' ? 'ticked on the site' : k.basis === 'staff' ? 'given to staff' : 'legacy list'} · {dateTime(k.grantedAt)}
                    </span>
                    {k.note && <span style={{ display: 'block', color: color.muted }}>{k.note}</span>}
                  </span>
                  {canUpdate && (
                    <button
                      onClick={() => revokeConsent.mutate({ id: c.id, purpose: k.purpose as Consent['purpose'] })}
                      disabled={busy}
                      style={{ background: 'transparent', border: 'none', color: color.red, fontSize: 12, fontWeight: 700, cursor: 'pointer', flex: 'none' }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
            {canUpdate && (
              <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                <Field label="Record consent">
                  <select value={consentPurpose} onChange={(e) => setConsentPurpose(e.target.value as Consent['purpose'])} style={{ ...selectStyle, fontSize: 13 }}>
                    {(Object.keys(PURPOSE_LABEL) as Consent['purpose'][]).map((p) => (
                      <option key={p} value={p}>{PURPOSE_LABEL[p]}</option>
                    ))}
                  </select>
                </Field>
                <Field label="How it was given (required)">
                  <input
                    value={consentNote}
                    onChange={(e) => setConsentNote(e.target.value)}
                    placeholder="e.g. Asked on the 4 Sep call"
                    style={{ ...selectStyle, backgroundImage: 'none', fontSize: 13 }}
                  />
                </Field>
                <Button
                  disabled={busy || consentNote.trim().length < 3}
                  onClick={() => {
                    recordConsent.mutate({ id: c.id, purpose: consentPurpose, note: consentNote.trim() });
                    setConsentNote('');
                  }}
                  style={{ justifySelf: 'start' }}
                >
                  Record
                </Button>
              </div>
            )}
          </section>

          {canUpdate && (
            <section style={{ borderTop: `1px solid ${color.line}`, paddingTop: 14 }}>
              {c.suppressedAt ? (
                lifting ? (
                  <LiftStop
                    theirs={c.suppressionSource === 'person' || isReasonRequired(setSuppressed.error)}
                    reason={liftReason}
                    onReason={setLiftReason}
                    busy={busy}
                    onLift={() =>
                      setSuppressed.mutate(
                        { id: c.id, suppressed: false, reason: liftReason.trim() || undefined },
                        {
                          onSuccess: () => {
                            setLifting(false);
                            setLiftReason('');
                          },
                        },
                      )
                    }
                    onCancel={() => {
                      setLifting(false);
                      setLiftReason('');
                      setSuppressed.reset();
                    }}
                  />
                ) : (
                  <Button onClick={() => setLifting(true)} disabled={busy}>
                    Clear do-not-contact
                  </Button>
                )
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  <Field label="Mark as do-not-contact">
                    <input
                      value={suppressNote}
                      onChange={(e) => setSuppressNote(e.target.value)}
                      maxLength={STOP_TEXT_MAX}
                      placeholder="Why (optional), e.g. asked by phone"
                      style={{ ...selectStyle, backgroundImage: 'none', fontSize: 13 }}
                    />
                  </Field>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      setSuppressed.mutate(
                        { id: c.id, suppressed: true, note: suppressNote.trim() || undefined },
                        { onSuccess: () => setSuppressNote('') },
                      )
                    }
                    style={{ justifySelf: 'start', background: color.red }}
                  >
                    Stop contacting
                  </Button>
                </div>
              )}
            </section>
          )}

          {(setTags.error || setOwner.error || setSuppressed.error || notDuplicate.error || recordConsent.error || revokeConsent.error) && (
            <ErrorNote
              message={String(
                ((setTags.error ?? setOwner.error ?? setSuppressed.error ?? notDuplicate.error ?? recordConsent.error ?? revokeConsent.error) as Error).message,
              )}
            />
          )}
        </div>

        {/* ── History ── */}
        <section style={{ minWidth: 0 }}>
          <Eyebrow tone="blue">History</Eyebrow>
          <div style={{ display: 'grid', gap: 0 }}>
            {timeline.length === 0 && <span style={{ fontSize: 12.5, color: color.muted }}>Nothing yet.</span>}
            {timeline.map((e, i) => {
              const tone = TYPE_TONE[e.type];
              const clickable = e.type === 'submission' && e.submissionId && onOpenSubmission;
              return (
                <div
                  key={i}
                  onClick={() => clickable && onOpenSubmission!(e.submissionId!, e.form!)}
                  className={clickable ? 'hov-row' : undefined}
                  style={{
                    display: 'grid', gridTemplateColumns: '70px 1fr', gap: 10, alignItems: 'start',
                    padding: '9px 4px', borderBottom: `1px solid ${color.line}`,
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontSize: 11.5, color: color.muted, paddingTop: 3 }}>{shortDate(e.at)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: tone.bg, color: tone.fg }}>
                        {tone.label}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: color.ink }}>{e.title}</span>
                      {e.status && <span style={{ fontSize: 11.5, color: color.muted }}>· {statusLabelOf(m, e.status)}</span>}
                    </span>
                    {e.detail && <span style={{ display: 'block', fontSize: 12.5, color: color.muted, marginTop: 2 }}>{e.detail}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── In progress ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          <section>
            <Eyebrow tone="blue">In progress</Eyebrow>
            {c.records.length === 0 && (
              <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
                {c.newsletter === 'active' ? 'On the newsletter, and nothing else.' : 'Nothing on record in your studies.'}
              </p>
            )}
            <RecordGroup title="Studies" records={boards} meta={m} onOpen={open} />
            <RecordGroup title="Patients they referred" records={referrals} meta={m} onOpen={open} />
            <RecordGroup title="Messages" records={messages} meta={m} onOpen={open} />
          </section>
          {L && <WhatTheyToldUs objects={objects.data} L={L} onOpenSubmission={onOpenSubmission} />}
        </div>
      </div>
    );
  }

  return (
    <Modal header={header} onClose={onClose} width={1180} label="Contact">
      {body()}
    </Modal>
  );
}

/**
 * Lifting a stop. A stop the person asked for themselves needs a reason, and
 * the button waits for one; a stop staff set may say why, or not.
 */
function LiftStop({
  theirs,
  reason,
  onReason,
  busy,
  onLift,
  onCancel,
}: {
  theirs: boolean;
  reason: string;
  onReason: (v: string) => void;
  busy: boolean;
  onLift: () => void;
  onCancel: () => void;
}) {
  const missing = theirs && reason.trim().length === 0;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {theirs && (
        <p style={{ fontSize: 12.5, color: color.ink, margin: 0 }}>
          They asked us themselves to stop. Say why the stop is being lifted; the reason stays on their record
          with your name.
        </p>
      )}
      <Field label={theirs ? 'Why the stop is being lifted' : 'Why the stop is being lifted (optional)'}>
        <input
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          maxLength={STOP_TEXT_MAX}
          autoFocus
          placeholder={theirs ? 'e.g. They called on 20 Oct and asked to hear about studies again' : 'e.g. Marked by mistake'}
          style={{ ...selectStyle, backgroundImage: 'none', fontSize: 13 }}
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button disabled={busy || missing} onClick={onLift} title={missing ? 'Say why first' : undefined}>
          Lift the stop
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt style={{ fontSize: 12, color: color.muted }}>{label}</dt>
      <dd style={{ fontSize: 12.5, fontWeight: 700, color: color.ink, margin: 0 }}>{children}</dd>
    </>
  );
}

const PLACE: Record<ContactRecord['area'], string> = {
  inbox: 'Waiting in Forms submission',
  study: 'On the study board',
  referral: 'A patient they referred',
  message: 'In Messages',
};

/**
 * One kind of record, each as a card: what it is, where it stands, since
 * when, and a way to open it. Live ones first, as the API orders them, and
 * records that say the same thing folded into one card with a count.
 */
function RecordGroup({
  title,
  records,
  meta,
  onOpen,
}: {
  title: string;
  records: ContactRecord[];
  meta: MetaResponse;
  onOpen: (submissionId: string, form: string) => void;
}) {
  if (records.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ fontSize: 11.5, fontWeight: 800, color: color.muted, margin: '0 0 6px' }}>{title}</p>
      <div style={{ display: 'grid', gap: 8 }}>
        {groupRecords(records).map(({ first: r, count }) => {
          const name =
            r.area === 'message'
              ? messageOne(meta, r.form)
              : (r.study?.label ?? meta.forms[r.form]?.label ?? r.form);
          return (
            <div
              key={`${r.submissionId}-${r.via}`}
              style={{
                border: `1px solid ${color.line}`,
                borderLeft: `3px solid ${r.group === 'closed' ? color.line : r.group === 'parked' ? '#e8a256' : color.blue}`,
                borderRadius: 12,
                padding: '10px 12px',
                opacity: r.group === 'closed' ? 0.85 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: color.ink, minWidth: 0 }}>{name}</span>
                <button
                  type="button"
                  onClick={() => onOpen(r.submissionId, r.form)}
                  className="hov-underline"
                  style={{ background: 'none', border: 0, padding: 0, fontSize: 12, fontWeight: 700, color: color.blue, cursor: 'pointer', flex: 'none' }}
                >
                  {count > 1 ? 'Open newest' : 'Open'}
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                <StatusPill meta={meta} record={r} />
                {count > 1 && (
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: color.muted }}>{count} forms like this</span>
                )}
              </div>
              <p style={{ fontSize: 11.5, color: color.muted, margin: '6px 0 0' }}>
                {PLACE[r.area]}
                {r.via === 'referred' ? ' · referred by a clinician' : ''}
                {r.endorsedFrom ? ` · endorsed from ${r.endorsedFrom.short}` : ''}
                {count > 1 ? ' · newest ' : ' · since '}
                {shortDate(r.updatedAt)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ROUTE_LABEL: Record<ContactObjects['training'][number]['route'], string> = {
  rotation: 'Resident rotation',
  student: 'Student / observer',
  fellowship: 'Fellowship',
};

/**
 * The answers a person gave that the lab acts on, rather than the forms they
 * arrived in: how to reach them, what their visit day needs, what training
 * they asked about.
 *
 * Renders nothing at all for someone who has told us none of these — most
 * people — so the drawer does not grow an empty section for every contact.
 */
function WhatTheyToldUs({
  objects,
  L,
  onOpenSubmission,
}: {
  objects: ContactObjects | undefined;
  L: Lookup;
  onOpenSubmission?: (submissionId: string, form: string) => void;
}) {
  if (!objects) return null;
  const { pref, access, training, fits, interests, referrals, redacted } = objects;
  if (
    !pref && access.length === 0 && training.length === 0 &&
    fits.length === 0 && interests.length === 0 && referrals.length === 0
  ) {
    return null;
  }

  const v = (key: string, value: string | null) => (value == null ? null : L.valueLabel(key, value));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Eyebrow tone="blue">What they have told us</Eyebrow>
        {redacted && <RedactedBadge />}
      </div>
      <div style={{ display: 'grid', gap: 10, marginTop: 6 }}>
        {pref && (
          <Answer
            title="How to reach them"
            since={pref.since}
            onOpen={onOpenSubmission && (() => onOpenSubmission(pref.submissionId, 'how-to-reach-me'))}
            rows={[
              ['Channel', v('pref.channel', pref.channel)],
              ['Days', v('pref.days', pref.days)],
              ['Language', v('pref.language', pref.language)],
              ['Family may take the call', v('pref.family_ok', pref.familyOk)],
              ["Family member's first name", pref.familyFirst],
            ]}
          />
        )}
        {access.map((a) => (
          <Answer
            key={a.id}
            title={a.visitDate ? `Visit-day needs · ${plainDate(a.visitDate)}` : 'Visit-day needs'}
            since={a.createdAt}
            onOpen={onOpenSubmission && (() => onOpenSubmission(a.submissionId, 'access-interpreter'))}
            rows={[
              ['Language', v('access.language', a.language)],
              ['Getting around', a.mobility === '—' ? '—' : v('access.mobility', a.mobility)],
              ['Someone with them', v('access.companion', a.companion)],
              ['Note', a.note],
            ]}
          />
        ))}
        {referrals.map((r) => (
          <Answer
            key={r.submissionId}
            title={r.as === 'patient' ? 'Referred by a clinician' : 'Referred a patient'}
            since={r.createdAt}
            onOpen={onOpenSubmission && (() => onOpenSubmission(r.submissionId, 'referral'))}
            rows={[
              ['Study', r.studySlug ? L.studyShort(r.studySlug) : null],
              ['Clinician acknowledged', r.ackAt ? fullDate(r.ackAt) : 'Not yet'],
              ['Patient reached', r.as === 'patient' ? (r.outreachAt ? fullDate(r.outreachAt) : 'Not yet') : null],
              ['Updates to the clinician', r.clinicianUpdateOk ? 'Authorized, on file' : 'Not authorized'],
            ]}
          />
        ))}
        {interests.length > 0 && (
          <Answer
            title="Lists they are on"
            since={interests[interests.length - 1]!.createdAt}
            rows={interests.map((i): [string, string | null] => [
              interestListLabel[i.list] ?? i.list,
              [
                i.topics.map((t) => (t === 'any' ? 'Any future study' : L.studyShort(t))).join(', ') || null,
                i.unsubscribedAt ? `left ${fullDate(i.unsubscribedAt)}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'Subscribed',
            ])}
          />
        )}
        {fits.map((f) => (
          <Answer
            key={f.submissionId}
            title="Asked whether a patient would fit"
            since={f.createdAt}
            onOpen={onOpenSubmission && (() => onOpenSubmission(f.submissionId, 'pre-referral-fit'))}
            rows={[
              ['Study', f.studySlug ? L.studyShort(f.studySlug) : null],
              ['Question', v('fit.kind', f.kind)],
              ['Situation', f.sketch],
              ['May we reply', v('fit.reply_ok', f.replyOk)],
            ]}
          />
        ))}
        {training.map((t) => (
          <Answer
            key={t.id}
            title={ROUTE_LABEL[t.route]}
            since={t.createdAt}
            onOpen={
              onOpenSubmission &&
              (() =>
                onOpenSubmission(
                  t.submissionId,
                  t.route === 'rotation' ? 'resident-rotation' : t.route === 'student' ? 'student-observer' : 'fellowship',
                ))
            }
            rows={[
              ['Track', v('training.track', t.track)],
              ['Program', t.program],
              ['PGY', t.pgy],
              ['Year', t.year],
              ['Asking about', v('training.ask', t.ask)],
              ['Program director', t.pdName && t.pdEmail && t.pdName !== '—' ? `${t.pdName} · ${t.pdEmail}` : t.pdName],
              ['Dates', t.dates],
              ['On site', t.onsite],
              ['Hours', t.hours],
              ['Timing', t.timing],
              ['Hopes to', t.hope],
              ['Skills', t.skills],
              ['CV', v('training.cv', t.cv)],
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function Answer({
  title,
  since,
  rows,
  onOpen,
}: {
  title: string;
  since: string;
  rows: Array<[string, string | null]>;
  onOpen?: () => void;
}) {
  const shown = rows.filter(([, value]) => value !== null && value !== '');
  return (
    <div style={{ border: `1px solid ${color.line}`, borderRadius: 12, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: color.ink }}>{title}</span>
        <span style={{ fontSize: 11.5, color: color.muted }}>
          {shortDate(since)}
          {onOpen && (
            <>
              {' · '}
              <button
                type="button"
                onClick={onOpen}
                style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: color.blue, cursor: 'pointer' }}
              >
                Open form
              </button>
            </>
          )}
        </span>
      </div>
      <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(110px,auto) 1fr', gap: '3px 12px', margin: '8px 0 0' }}>
        {shown.map(([label, value]) => (
          <div key={label} style={{ display: 'contents' }}>
            <dt style={{ fontSize: 12, color: color.muted }}>{label}</dt>
            <dd style={{ fontSize: 12.5, color: color.ink, margin: 0, overflowWrap: 'anywhere' }}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
